import type { BrandKey } from '../components/ui/BrandLogos';
import project1 from '../assets/project_1.png';
import project2 from '../assets/project_2.png';
import project3 from '../assets/project_3.png';
import project4 from '../assets/project_4.png';
import project5 from '../assets/project_5.png';
import project6 from '../assets/project_6.png';
import project7 from '../assets/project_7.png';
import project8 from '../assets/project_8.png';

export interface CaseStudy {
    /** URL segment — the page lives at `#/work/<slug>`. */
    slug: string;
    title: string;
    /** Short blurb used on the card in the Selected work grid. */
    description: string;
    tags: string[];
    metric: { value: string; label: string };
    image: string;
    /** Renders as a double-width card in the grid. */
    large?: boolean;

    // ── detail page ──────────────────────────────────────────────────────────
    industry: string;
    timeline: string;
    services: string[];
    /** Lead paragraph under the title. */
    summary: string;
    /** "The challenge" — one paragraph per entry. */
    challenge: string[];
    /** "How we built it" — the numbered steps of the delivered system. */
    approach: { title: string; body: string }[];
    /** Metric band on the detail page. Include the card metric as the first entry. */
    results: { value: string; label: string }[];
    /** Tools the system runs on. `brand` renders the real logo when one exists. */
    stack: { label: string; brand?: BrandKey }[];
    /** Optional YouTube or Loom URL — renders a player above the CTA. */
    video?: string;
}

export const caseStudies: CaseStudy[] = [
    {
        slug: 'whatsapp-broadcast-system',
        title: 'Automated WhatsApp Broadcast System',
        description:
            'Scrapes daily news, summarises it with GPT-4, and broadcasts tailored updates to 5,000+ subscribers via the WhatsApp API — fully hands-off.',
        tags: ['WhatsApp API', 'OpenAI', 'Python'],
        metric: { value: '5,000+', label: 'subscribers reached daily' },
        image: project1,
        large: true,
        industry: 'Media & publishing',
        timeline: '5 weeks to launch',
        services: ['Workflow engineering', 'AI content pipeline', 'WhatsApp Business API'],
        summary:
            'A daily news digest that used to take a two-person editorial team most of the morning now researches, writes, segments and sends itself before anyone opens a laptop.',
        challenge: [
            'The team was manually reading a dozen sources every morning, rewriting the highlights into a digest, then pasting it into WhatsApp Business and firing it at broadcast lists one group at a time. The whole ritual ate three to four hours a day and still slipped whenever someone was travelling or unwell.',
            'Growth made it worse rather than better. Every new subscriber segment meant another list to maintain and another copy of the message to tailor by hand, so the cost of publishing rose in lockstep with the audience — exactly the wrong shape for a business trying to scale reach.',
        ],
        approach: [
            {
                title: 'Source ingestion',
                body: 'A scheduled scraper pulls from the publication\'s approved sources on a fixed cadence, normalises each article into a common shape, and de-duplicates stories that appear across several outlets so the same news never lands twice.',
            },
            {
                title: 'AI summarisation with an editorial voice',
                body: 'Each cluster of stories is passed to GPT-4 with a prompt carrying the publication\'s house style, length limits and formatting rules. The model returns a ranked digest rather than loose prose, which keeps the output predictable enough to publish without a rewrite.',
            },
            {
                title: 'Segmentation',
                body: 'Subscribers are tagged by interest and language, and the digest is assembled per segment — so a finance subscriber and a sports subscriber get genuinely different messages built from the same run, at no extra editorial cost.',
            },
            {
                title: 'Compliant broadcast delivery',
                body: 'Messages go out through the official WhatsApp Business API using pre-approved templates, with rate limiting and retry-with-backoff so a transient failure never drops a segment. Opt-outs are honoured automatically and written back to the subscriber list.',
            },
            {
                title: 'Editorial safety net',
                body: 'Nothing sends silently. The assembled digest lands in a review channel with a short approval window; if an editor wants to intervene they can, and if nobody touches it, it ships on schedule.',
            },
        ],
        results: [
            { value: '5,000+', label: 'subscribers reached daily' },
            { value: '~3.5 hrs', label: 'editorial time saved per day' },
            { value: '100%', label: 'on-time delivery since launch' },
            { value: '0', label: 'manual sends required' },
        ],
        stack: [
            { label: 'WhatsApp Business API', brand: 'whatsapp' },
            { label: 'OpenAI', brand: 'openai' },
            { label: 'Python' },
            { label: 'PostgreSQL', brand: 'postgresql' },
        ],
    },
    {
        slug: 'lead-capture-crm-sync',
        title: 'Lead Capture & CRM Sync',
        description: 'Unified lead ingestion from Facebook Ads, website & LinkedIn into HubSpot with auto lead-scoring.',
        tags: ['HubSpot', 'Zapier'],
        metric: { value: '3×', label: 'faster sales response' },
        image: project2,
        industry: 'B2B services',
        timeline: '3 weeks',
        services: ['Integration engineering', 'Lead scoring', 'Sales enablement'],
        summary:
            'Three lead sources that lived in three different inboxes now arrive in one HubSpot pipeline, scored, deduplicated and assigned before a rep has finished their coffee.',
        challenge: [
            'Leads came in from Facebook Lead Ads, the website contact form and LinkedIn outreach — each landing somewhere different. Someone exported CSVs a few times a week and pasted them into the CRM, which meant the freshest lead in the system was routinely two days old.',
            'Because nothing was deduplicated, the same person could exist three times under slightly different spellings, and two reps could unknowingly work the same account. Response time was the real casualty: the leads most likely to convert were the ones going cold in a spreadsheet.',
        ],
        approach: [
            {
                title: 'One ingestion path',
                body: 'Every source writes into a single normalised intake step — form submissions, Lead Ads webhooks and LinkedIn exports all land in the same shape, so downstream logic is written once rather than three times.',
            },
            {
                title: 'Deduplication and enrichment',
                body: 'Incoming records are matched against existing HubSpot contacts on email and normalised phone number before anything is created. Matches update the existing record and append the new touchpoint instead of spawning a duplicate.',
            },
            {
                title: 'Automatic lead scoring',
                body: 'A scoring model built from the client\'s own closed-won history weights source, company size, stated budget and engagement recency, then writes a score and tier onto the contact so the pipeline sorts itself.',
            },
            {
                title: 'Routing and instant acknowledgement',
                body: 'High-tier leads are assigned round-robin to available reps and pushed to Slack within seconds, while the lead receives an immediate branded acknowledgement — the single change that moved response time the most.',
            },
        ],
        results: [
            { value: '3×', label: 'faster sales response' },
            { value: '0', label: 'CSV exports per week (was ~6)' },
            { value: '~11%', label: 'of intake caught as duplicates' },
            { value: '< 60s', label: 'lead-to-CRM latency' },
        ],
        stack: [
            { label: 'HubSpot', brand: 'hubspot' },
            { label: 'Zapier', brand: 'zapier' },
            { label: 'Slack', brand: 'slack' },
            { label: 'Meta Lead Ads' },
        ],
    },
    {
        slug: 'ecommerce-inventory-sync',
        title: 'E-commerce Inventory Sync',
        description: 'Real-time two-way sync between Shopify and warehouse stock.',
        tags: ['Shopify', 'n8n'],
        metric: { value: '-95%', label: 'stockouts' },
        image: project3,
        industry: 'Retail & e-commerce',
        timeline: '4 weeks',
        services: ['Integration engineering', 'Inventory operations', 'Monitoring'],
        summary:
            'The storefront and the warehouse finally agree on what is in stock — continuously, in both directions, without a nightly CSV in sight.',
        challenge: [
            'Stock levels lived in the warehouse management system and were pushed to Shopify by an overnight export. For most of the trading day the storefront was quoting yesterday\'s numbers, which meant overselling fast-moving lines and then apologising to customers.',
            'The reverse direction was worse: sales did not decrement warehouse stock until the next morning\'s reconciliation, so pickers regularly walked to empty shelves. Every mismatch cost time twice — once in the warehouse and once in customer support.',
        ],
        approach: [
            {
                title: 'Event-driven, not batch',
                body: 'Shopify order and fulfilment webhooks trigger a workflow the moment something sells, and warehouse stock adjustments publish the same way. Nothing waits for a nightly window.',
            },
            {
                title: 'A reconciliation ledger',
                body: 'Rather than blindly overwriting quantities, every change is written as a delta against a ledger. That makes the sync idempotent — a replayed or duplicated webhook cannot corrupt the count — and gives operations an audit trail when a number looks wrong.',
            },
            {
                title: 'Conflict rules that match the business',
                body: 'When both sides move at once, the warehouse is treated as the source of truth for physical count while Shopify owns reserved-but-unfulfilled quantities. Buffer thresholds on the highest-velocity SKUs absorb the remaining race conditions.',
            },
            {
                title: 'Drift detection',
                body: 'A scheduled comparison sweeps both systems and reports any SKU that has drifted beyond tolerance to Slack, so a silent integration failure surfaces in minutes instead of at month-end.',
            },
        ],
        results: [
            { value: '-95%', label: 'stockouts' },
            { value: '< 30s', label: 'storefront stock latency' },
            { value: '24/7', label: 'two-way sync coverage' },
            { value: '0', label: 'nightly CSV jobs remaining' },
        ],
        stack: [
            { label: 'Shopify', brand: 'shopify' },
            { label: 'n8n', brand: 'n8n' },
            { label: 'PostgreSQL', brand: 'postgresql' },
            { label: 'Slack', brand: 'slack' },
        ],
    },
    {
        slug: 'crm-mls-integration',
        title: 'CRM ↔ MLS Integration Pipeline',
        description: 'Automated real-estate data sync between CRMs and Multiple Listing Services.',
        tags: ['n8n', 'Salesforce'],
        metric: { value: '-95%', label: 'manual data entry' },
        image: project4,
        industry: 'Real estate',
        timeline: '6 weeks',
        services: ['Data pipeline', 'CRM integration', 'Field mapping'],
        summary:
            'Listing data moves between the MLS feed and the agency CRM on its own, so agents stop retyping property records and start trusting what the CRM tells them.',
        challenge: [
            'Agents were copying listing details out of the MLS and into Salesforce by hand — address, price, status, media, agent of record. Each property took several minutes and each retype was a chance to introduce an error that nobody would catch until a client saw it.',
            'The two systems also disagreed about shape. MLS feeds arrive with their own field names, enumerations and media conventions, and none of it lines up with a CRM object model designed around deals and contacts rather than properties.',
        ],
        approach: [
            {
                title: 'A declarative field map',
                body: 'Every MLS field is mapped to its CRM counterpart in one configuration file, with the transformations — unit conversion, status enumeration, agent lookup — expressed alongside it. Adding a board or changing a field is a config edit, not a code change.',
            },
            {
                title: 'Incremental sync',
                body: 'The pipeline tracks a high-water mark per feed and pulls only records modified since the last successful run, which keeps a full-portfolio sync inside the feed\'s rate limits instead of hammering it.',
            },
            {
                title: 'Media handling',
                body: 'Listing photos are fetched, deduplicated by content hash and attached to the CRM record, so the same image is not stored a dozen times across a portfolio.',
            },
            {
                title: 'Validation before write',
                body: 'Records that fail validation — missing a required field, an unmappable status, a price that moved by an implausible amount — are quarantined for review rather than written. Good data flows; suspicious data gets a human.',
            },
        ],
        results: [
            { value: '-95%', label: 'manual data entry' },
            { value: '15 min', label: 'sync interval, all boards' },
            { value: '1', label: 'config file to onboard a new feed' },
            { value: '100%', label: 'of writes validated first' },
        ],
        stack: [
            { label: 'n8n', brand: 'n8n' },
            { label: 'Salesforce', brand: 'salesforce' },
            { label: 'MLS / RETS feeds' },
            { label: 'PostgreSQL', brand: 'postgresql' },
        ],
    },
    {
        slug: 'ai-whatsapp-business-automation',
        title: 'AI WhatsApp Business Automation',
        description:
            '24/7 WhatsApp AI agents that handle orders, inquiries & support with smart hand-off to humans when it matters.',
        tags: ['n8n', 'OpenAI API', 'WhatsApp'],
        metric: { value: '80%', label: 'inquiries auto-resolved' },
        image: project5,
        large: true,
        industry: 'Retail & customer service',
        timeline: '6 weeks',
        services: ['AI agent development', 'WhatsApp Business API', 'Human hand-off design'],
        summary:
            'An AI agent that answers on WhatsApp at 2am, takes the order, checks the stock, and knows exactly when to stop talking and fetch a human.',
        challenge: [
            'Customers message on WhatsApp at every hour, and the majority ask a small number of predictable things: is this in stock, how much is delivery, where is my order, can I change it. A small team was answering the same questions all day and still losing the conversations that came in overnight.',
            'Off-the-shelf chatbots had been tried and abandoned. They handled the scripted path and fell apart everywhere else, and — worse — they hid the escalation route, so a frustrated customer had no way to reach a person.',
        ],
        approach: [
            {
                title: 'Grounded answers, not guesses',
                body: 'The agent answers from the client\'s own catalogue, pricing and policy documents through retrieval, so it quotes what is actually true today. When the answer is not in the source material, it says so rather than inventing one.',
            },
            {
                title: 'Real actions, not just replies',
                body: 'The agent is given a small set of tools — check stock, create an order, look up an order status, schedule a callback — each with strict validation. It can complete a transaction end to end without a human touching it.',
            },
            {
                title: 'Hand-off as a first-class path',
                body: 'Explicit triggers route a conversation to a person: an escalation request, a refund or complaint, repeated failure to resolve, or low model confidence. The human receives the full transcript and context, so the customer never repeats themselves.',
            },
            {
                title: 'Business hours awareness',
                body: 'Outside staffed hours the agent sets accurate expectations and queues the conversation for the morning rather than promising an instant human. Inside them, hand-off is immediate.',
            },
            {
                title: 'Continuous review',
                body: 'Every escalated or low-confidence conversation is logged for weekly review. Those transcripts drive prompt and knowledge-base updates, so the auto-resolution rate climbs as the system is used.',
            },
        ],
        results: [
            { value: '80%', label: 'inquiries auto-resolved' },
            { value: '24/7', label: 'first-response coverage' },
            { value: '< 10s', label: 'median first reply' },
            { value: '100%', label: 'of escalations carry full context' },
        ],
        stack: [
            { label: 'n8n', brand: 'n8n' },
            { label: 'OpenAI', brand: 'openai' },
            { label: 'WhatsApp Business API', brand: 'whatsapp' },
            { label: 'Supabase', brand: 'supabase' },
        ],
    },
    {
        slug: 'ai-call-agent',
        title: 'Human-Like AI Call Agent',
        description: 'Voice agent that answers calls, takes orders & supports customers — indistinguishable from human.',
        tags: ['Voice AI', 'VoIP'],
        metric: { value: '24/7', label: 'call coverage' },
        image: project6,
        industry: 'Hospitality & services',
        timeline: '7 weeks',
        services: ['Voice AI', 'Telephony integration', 'Conversation design'],
        summary:
            'Every call gets answered on the first ring — including the ones that used to go to voicemail at closing time — by a voice agent that takes the order and books the callback itself.',
        challenge: [
            'Missed calls were straightforwardly lost revenue. During peak hours staff were already serving customers in person, and outside opening hours the line simply rang out. Callers rarely left a voicemail and rarely called back.',
            'Voice raises the bar that text does not. Latency that would be invisible in chat is excruciating on a phone call, and a caller who has to repeat themselves twice will hang up. The agent had to sound natural and interruptible, or it was worse than the voicemail it replaced.',
        ],
        approach: [
            {
                title: 'Low-latency speech pipeline',
                body: 'Streaming speech-to-text, a response model and streaming text-to-speech are chained so the agent begins speaking while it is still forming the rest of the reply, keeping turn-taking inside the window where a conversation still feels natural.',
            },
            {
                title: 'Barge-in handling',
                body: 'Callers interrupt. The agent detects speech over its own output, stops talking immediately and re-plans from what it just heard — the single behaviour that most separates a natural call from an obvious robot.',
            },
            {
                title: 'Grounded, scripted where it matters',
                body: 'Menu, pricing, hours and availability come from live business data. Compliance-sensitive phrasing is fixed script rather than generated, so the parts that must be exact always are.',
            },
            {
                title: 'Confirm before commit',
                body: 'Orders and bookings are read back to the caller for confirmation before anything is written, and the caller receives a written summary by SMS or WhatsApp when the call ends.',
            },
            {
                title: 'Warm transfer',
                body: 'When a call needs a person, the agent transfers to a human with a spoken summary of what has happened so far, so the caller does not start over.',
            },
        ],
        results: [
            { value: '24/7', label: 'call coverage' },
            { value: '0', label: 'calls to voicemail' },
            { value: '~700ms', label: 'median response latency' },
            { value: '1st ring', label: 'answer time, every call' },
        ],
        stack: [
            { label: 'Voice AI (STT / TTS)' },
            { label: 'VoIP / SIP telephony' },
            { label: 'OpenAI', brand: 'openai' },
            { label: 'n8n', brand: 'n8n' },
        ],
    },
    {
        slug: 'dms-workflow-automation',
        title: 'DMS Workflow Automation',
        description: 'Document processing that categorises & routes files by content and metadata.',
        tags: ['Make', 'OCR'],
        metric: { value: '10×', label: 'faster processing' },
        image: project7,
        industry: 'Professional services',
        timeline: '4 weeks',
        services: ['Document AI', 'OCR pipeline', 'Workflow engineering'],
        summary:
            'Incoming documents read, classify, name and file themselves — and the exceptions go to a person instead of the whole pile.',
        challenge: [
            'Documents arrived as email attachments, scans and uploads, and every one was opened, identified, renamed by hand and dragged into the right folder. It was slow, mind-numbing work, and the naming conventions drifted the moment more than one person was doing it.',
            'The cost was not only the filing. Because retrieval depended on someone having filed correctly months earlier, finding a specific document was its own small search operation.',
        ],
        approach: [
            {
                title: 'One intake, many sources',
                body: 'A monitored mailbox, a watched folder and an upload endpoint all feed the same pipeline, so a document is handled identically no matter how it arrived.',
            },
            {
                title: 'OCR with layout awareness',
                body: 'Scans and image PDFs are run through OCR that preserves layout, which is what makes reliable field extraction possible on invoices and forms where position carries meaning.',
            },
            {
                title: 'Classification and extraction',
                body: 'A model assigns the document type and pulls the fields that matter for that type — dates, reference numbers, counterparties, totals — returning structured data rather than a wall of text.',
            },
            {
                title: 'Deterministic naming and routing',
                body: 'Extracted fields drive a strict naming convention and the destination folder, so filing is consistent by construction and search works on the filename alone.',
            },
            {
                title: 'Confidence-gated review',
                body: 'Anything the pipeline is not confident about — an unrecognised type, a failed extraction, a suspicious total — is queued for human review with the extracted values pre-filled, so a person corrects rather than retypes.',
            },
        ],
        results: [
            { value: '10×', label: 'faster processing' },
            { value: '1 convention', label: 'applied to every document' },
            { value: '~8%', label: 'routed to human review' },
            { value: '0', label: 'manual renaming' },
        ],
        stack: [
            { label: 'Make', brand: 'make' },
            { label: 'OCR engine' },
            { label: 'OpenAI', brand: 'openai' },
            { label: 'Google Drive' },
        ],
    },
    {
        slug: 'multi-platform-lead-distribution',
        title: 'Multi-Platform Lead Distribution',
        description: 'Intelligent routing that distributes prospects across channels by criteria & availability.',
        tags: ['Zapier', 'Pipedrive'],
        metric: { value: '100%', label: 'leads routed instantly' },
        image: project8,
        industry: 'Multi-branch sales',
        timeline: '3 weeks',
        services: ['Lead routing', 'CRM integration', 'SLA monitoring'],
        summary:
            'Every lead reaches the right rep in the right territory the moment it lands — and if that rep goes quiet, it moves on without anyone noticing it stalled.',
        challenge: [
            'Leads were distributed by a manager reading a shared inbox and forwarding each one by hand. That worked at low volume and broke completely at high volume, on weekends, and whenever the manager was in a meeting.',
            'Fairness and coverage were impossible to verify. Nobody could say with confidence whether leads were being spread evenly, whether a territory was being neglected, or how many had simply been sat on for a day.',
        ],
        approach: [
            {
                title: 'Rules the business actually uses',
                body: 'Routing is driven by territory, product line, deal size band and language — the same criteria the manager was applying from memory, now written down and applied identically every time.',
            },
            {
                title: 'Availability-aware assignment',
                body: 'The router respects working hours, time zones, holidays and current open-lead load, so leads are not assigned to someone who is offline or already buried.',
            },
            {
                title: 'Escalation on silence',
                body: 'Each assignment starts an SLA timer. If the lead is not actioned in time it is reassigned automatically and the manager is notified — the mechanism that turns "routed" into "actually worked".',
            },
            {
                title: 'Multi-channel notification',
                body: 'Assignments arrive wherever the rep already is — CRM task, Slack and email — so nobody has to keep a tab open to find out they have a new lead.',
            },
            {
                title: 'Distribution reporting',
                body: 'A weekly summary shows volume by source, distribution by rep and territory, and SLA breaches, which turns routing from a black box into something management can tune.',
            },
        ],
        results: [
            { value: '100%', label: 'leads routed instantly' },
            { value: '0', label: 'manual forwarding' },
            { value: 'SLA-backed', label: 'auto-reassign on no response' },
            { value: 'Weekly', label: 'distribution and SLA reporting' },
        ],
        stack: [
            { label: 'Zapier', brand: 'zapier' },
            { label: 'Pipedrive' },
            { label: 'Slack', brand: 'slack' },
            { label: 'Gmail', brand: 'gmail' },
        ],
    },
];

export function findCaseStudy(slug: string): CaseStudy | undefined {
    return caseStudies.find((c) => c.slug === slug);
}

/** Slug of the next case study, wrapping at the end — powers "Next project". */
export function nextCaseStudy(slug: string): CaseStudy {
    const i = caseStudies.findIndex((c) => c.slug === slug);
    return caseStudies[(i + 1) % caseStudies.length];
}
